import { IServiceProvider, IServicesManager } from '@/core/di/contracts';
import { ICommonHelpers } from '@/core/helpers/contracts';
import {
    IGlobalSettings,
    IPlugins,
    IPluginsUnloader,
    IVersionsManager,
    ImGlobal,
    ImGlobalObject,
    ImUtils,
    PluginTag,
} from '@/core/plugins/contracts';
import { IVersion, IVersionEx, imVersion } from '@/core/plugins/version';
import { IScripts, IScriptsUnloader } from '@/core/webScripts/contracts';
import { IpsApi } from '@/core/webapi/helpers';

export const buildBlankGlobalImObject = (): ImGlobalObject => {
    // Тут создается заготовка глобального объекта, которая будет расширена при последующей загрузке
    return {
        version: buildVersionManager(imVersion, [], []),
        tags: [] as PluginTag[],
        build: function build() {
            return buildGlobalVariables(this);
        },
        withTag: function withTag(tags) {
            return {
                ...this,
                tags,
                version: buildVersionManager(this.version.core.version, tags, this.version.registered),
                plugins: this.plugins.withTag(tags),
            };
        },
    } as ImGlobalObject;
};

export const assignBlankGlobalImObject = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
    (window as any).IM_PLUGINS_GLOBAL_OBJECT = buildBlankGlobalImObject();
};

const buildVersionManager = (
    curVersion: IVersion,
    tags: PluginTag[],
    versions: IVersionsManager['registered']
): IVersionsManager => {
    const find = (tagToFind: PluginTag[]): IVersionsManager['registered'][number] | undefined =>
        versions.find((existVersion) =>
            tagToFind.every((inTag) =>
                existVersion.tags.some((existTag) => existTag.key === inTag.key && existTag.value === inTag.value)
            )
        );

    const add = (version: IVersion): void => {
        if (!find(tags)) {
            versions.push({ version, tags });
        }
    };

    return {
        core: buildVersionEx(curVersion),
        registered: versions,
        add,
        getVersion: (tagsToFind) => find(tagsToFind)?.version,
    };
};

// Для проверки совместимости версий.
export const buildVersionEx = (curVersion: IVersion): IVersionEx => ({
    version: curVersion,
    compatibleWith(version: IVersion): boolean {
        // В текущей реализации отбраковываем только по мажорной версии
        return version.major === curVersion.major;
    },
    toString(): string {
        return `${curVersion.major}.${curVersion.minor}.${curVersion.patch}`;
    },
});

export const buildGlobalVariables = (obj: ImGlobalObject): ImGlobal => ({
    get Plugins(): IPlugins {
        return obj.plugins;
    },
    get PluginsUnloader(): IPluginsUnloader {
        return obj.plugins;
    },
    get Ioc(): IServiceProvider {
        return obj.ioc;
    },
    get ServicesManager(): IServicesManager {
        return obj.ioc;
    },
    get Helpers(): ICommonHelpers {
        return obj.helpers;
    },
    get Settings(): IGlobalSettings {
        return obj.settings;
    },
    get Utils(): ImUtils {
        return obj.utils;
    },
    get Scripts(): IScripts {
        return obj.imScripts;
    },
    get ScriptsUnloader(): IScriptsUnloader {
        return obj.imScripts;
    },
    get Api(): IpsApi {
        return obj.api;
    },
    withTag: (tags) => buildGlobalVariables(obj.withTag(tags)),
    init: (version): void => {
        obj.version.add(version);
    },
});
